import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Save, Eye, Settings, Upload, Video, FileText, Mic, Image, Pizza as Quiz, Trash2, GripVertical, Clock, Users } from 'lucide-react';
import { Card, CardHeader, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { Tabs } from '../ui/Tabs';

interface CourseModule {
  id: string;
  title: string;
  type: 'video' | 'text' | 'quiz' | 'interactive' | 'audio';
  duration: number;
  content: string;
  order: number;
}

interface CourseData {
  id: string;
  title: string;
  description: string;
  category: string;
  level: string;
  duration: number;
  modules: CourseModule[];
  settings: {
    isPublished: boolean;
    allowComments: boolean;
    requireCompletion: boolean;
    certificateEnabled: boolean;
  };
}

export function CourseBuilder() {
  const [course, setCourse] = useState<CourseData>({
    id: '',
    title: '',
    description: '',
    category: 'cpc',
    level: 'beginner',
    duration: 0,
    modules: [],
    settings: {
      isPublished: false,
      allowComments: true,
      requireCompletion: true,
      certificateEnabled: true
    }
  });

  const [showModuleModal, setShowModuleModal] = useState(false);
  const [editingModule, setEditingModule] = useState<CourseModule | null>(null);
  const [previewMode, setPreviewMode] = useState(false);

  const moduleTypes = [
    { type: 'video', label: 'Video Content', icon: Video, description: 'Upload or embed video content' },
    { type: 'text', label: 'Text Content', icon: FileText, description: 'Rich text with formatting' },
    { type: 'audio', label: 'Audio Content', icon: Mic, description: 'Audio narration or podcasts' },
    { type: 'quiz', label: 'Assessment', icon: Quiz, description: 'Interactive quizzes and tests' },
    { type: 'interactive', label: 'Interactive', icon: Settings, description: 'Simulations and activities' }
  ];

  const handleAddModule = (type: CourseModule['type']) => {
    const newModule: CourseModule = {
      id: `module_${Date.now()}`,
      title: `New ${type} Module`,
      type,
      duration: 5,
      content: '',
      order: course.modules.length + 1
    };
    
    setCourse(prev => ({
      ...prev,
      modules: [...prev.modules, newModule]
    }));
    
    setEditingModule(newModule);
    setShowModuleModal(true);
  };

  const handleEditModule = (module: CourseModule) => {
    setEditingModule(module);
    setShowModuleModal(true);
  };

  const handleSaveModule = (moduleData: Partial<CourseModule>) => {
    if (!editingModule) return;

    setCourse(prev => ({
      ...prev,
      modules: prev.modules.map(module =>
        module.id === editingModule.id
          ? { ...module, ...moduleData }
          : module
      )
    }));

    setShowModuleModal(false);
    setEditingModule(null);
  };

  const handleDeleteModule = (moduleId: string) => {
    setCourse(prev => ({
      ...prev,
      modules: prev.modules.filter(module => module.id !== moduleId)
    }));
  };

  const handleSaveCourse = () => {
    // Implement course saving logic
    console.log('Saving course:', course);
  };

  const handlePublishCourse = () => {
    setCourse(prev => ({
      ...prev,
      settings: { ...prev.settings, isPublished: true }
    }));
  };

  const courseInfoTab = (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Course Title"
          value={course.title}
          onChange={(e) => setCourse(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Enter course title"
          fullWidth
        />
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
          <select
            value={course.category}
            onChange={(e) => setCourse(prev => ({ ...prev, category: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="cpc">CPC Training</option>
            <option value="hazard">Hazard Perception</option>
            <option value="safety">Safety Training</option>
            <option value="regulations">Regulations</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={course.description}
          onChange={(e) => setCourse(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe what learners will gain from this course"
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Level</label>
          <select
            value={course.level}
            onChange={(e) => setCourse(prev => ({ ...prev, level: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        <Input
          label="Duration (hours)"
          type="number"
          value={course.duration}
          onChange={(e) => setCourse(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
          placeholder="0"
          fullWidth
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <Badge variant={course.settings.isPublished ? 'success' : 'warning'}>
            {course.settings.isPublished ? 'Published' : 'Draft'}
          </Badge>
        </div>
      </div>
    </div>
  );

  const modulesTab = (
    <div className="space-y-6">
      {/* Add Module Buttons */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Content Module</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {moduleTypes.map((moduleType) => {
            const Icon = moduleType.icon;
            return (
              <Card
                key={moduleType.type}
                hover
                onClick={() => handleAddModule(moduleType.type as CourseModule['type'])}
                className="cursor-pointer"
              >
                <CardContent className="p-4 text-center">
                  <Icon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <h4 className="font-medium text-gray-900 mb-1">{moduleType.label}</h4>
                  <p className="text-sm text-gray-600">{moduleType.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Existing Modules */}
      {course.modules.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Modules</h3>
          <div className="space-y-3">
            {course.modules.map((module, index) => {
              const ModuleIcon = moduleTypes.find(t => t.type === module.type)?.icon || FileText;
              return (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card hover>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2 text-gray-400">
                            <GripVertical className="w-4 h-4" />
                            <span className="text-sm font-medium">{module.order}</span>
                          </div>
                          
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <ModuleIcon className="w-5 h-5 text-blue-600" />
                          </div>
                          
                          <div>
                            <h4 className="font-medium text-gray-900">{module.title}</h4>
                            <div className="flex items-center space-x-3 text-sm text-gray-500">
                              <span className="capitalize">{module.type}</span>
                              <span className="flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {module.duration} min
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Settings className="w-4 h-4" />}
                            onClick={() => handleEditModule(module)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Trash2 className="w-4 h-4" />}
                            onClick={() => handleDeleteModule(module.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const settingsTab = (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
            <div>
              <h4 className="font-medium text-gray-900">Allow Comments</h4>
              <p className="text-sm text-gray-600">Let learners comment on course content</p>
            </div>
            <input
              type="checkbox"
              checked={course.settings.allowComments}
              onChange={(e) => setCourse(prev => ({
                ...prev,
                settings: { ...prev.settings, allowComments: e.target.checked }
              }))}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
            <div>
              <h4 className="font-medium text-gray-900">Require Completion</h4>
              <p className="text-sm text-gray-600">Learners must complete all modules</p>
            </div>
            <input
              type="checkbox"
              checked={course.settings.requireCompletion}
              onChange={(e) => setCourse(prev => ({
                ...prev,
                settings: { ...prev.settings, requireCompletion: e.target.checked }
              }))}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
            <div>
              <h4 className="font-medium text-gray-900">Certificate Enabled</h4>
              <p className="text-sm text-gray-600">Issue certificates upon completion</p>
            </div>
            <input
              type="checkbox"
              checked={course.settings.certificateEnabled}
              onChange={(e) => setCourse(prev => ({
                ...prev,
                settings: { ...prev.settings, certificateEnabled: e.target.checked }
              }))}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'info', label: 'Course Info', icon: <FileText className="w-4 h-4" />, content: courseInfoTab },
    { id: 'modules', label: 'Modules', icon: <Video className="w-4 h-4" />, content: modulesTab },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" />, content: settingsTab }
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Course Builder</h1>
          <p className="text-gray-600 mt-1">Create and manage your training courses</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            icon={<Eye className="w-4 h-4" />}
            onClick={() => setPreviewMode(!previewMode)}
          >
            Preview
          </Button>
          
          <Button
            variant="secondary"
            icon={<Save className="w-4 h-4" />}
            onClick={handleSaveCourse}
          >
            Save Draft
          </Button>
          
          <Button
            variant="primary"
            icon={<Upload className="w-4 h-4" />}
            onClick={handlePublishCourse}
          >
            Publish Course
          </Button>
        </div>
      </div>

      {/* Course Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{course.modules.length}</div>
            <div className="text-sm text-gray-600">Modules</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">
              {course.modules.reduce((total, module) => total + module.duration, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Minutes</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">0</div>
            <div className="text-sm text-gray-600">Enrollments</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">
              {course.settings.isPublished ? 'Live' : 'Draft'}
            </div>
            <div className="text-sm text-gray-600">Status</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card>
        <CardContent className="p-6">
          <Tabs tabs={tabs} defaultTab="info" />
        </CardContent>
      </Card>

      {/* Module Editor Modal */}
      <Modal
        isOpen={showModuleModal}
        onClose={() => setShowModuleModal(false)}
        title={`Edit ${editingModule?.type} Module`}
        size="lg"
      >
        {editingModule && (
          <div className="space-y-6">
            <Input
              label="Module Title"
              value={editingModule.title}
              onChange={(e) => setEditingModule(prev => prev ? { ...prev, title: e.target.value } : null)}
              fullWidth
            />
            
            <Input
              label="Duration (minutes)"
              type="number"
              value={editingModule.duration}
              onChange={(e) => setEditingModule(prev => prev ? { ...prev, duration: parseInt(e.target.value) || 0 } : null)}
              fullWidth
            />
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
              <textarea
                value={editingModule.content}
                onChange={(e) => setEditingModule(prev => prev ? { ...prev, content: e.target.value } : null)}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter module content..."
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowModuleModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => handleSaveModule(editingModule)}>
                Save Module
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}